import type { FilterControlOperator } from 'lib-analytics-types';

type OperatorAssets = {
  /** The display label of the operator. */
  readonly label: string;
  /** The order index to be used when sorting the operators. */
  readonly orderIndex: number;
  /** Whether the operator requires selecting values. */
  readonly requiresValues: boolean;
};

const operatorAssets: Record<FilterControlOperator, OperatorAssets> = {
  is_one: {
    label: 'any of the selected values',
    orderIndex: 1,
    requiresValues: false,
  },
  is_not_one: {
    label: 'none of the selected values',
    orderIndex: 2,
    requiresValues: true,
  },
  is_all: {
    label: 'all of the selected values',
    orderIndex: 3,
    requiresValues: true,
  },
  is_not_all: {
    label: 'not all of the selected values',
    orderIndex: 4,
    requiresValues: true,
  },
  is_empty: {
    label: 'no value',
    orderIndex: 5,
    requiresValues: false,
  },
  is_not_empty: {
    label: 'any value',
    orderIndex: 6,
    requiresValues: false,
  },
};

/**
 * Safely get the assets associated with a filter operator.
 *
 * In case the API returns a new filter operator that is not yet supported by
 * the frontend, we return the raw operator as the label and sensible defaults,
 * instead of throwing an error or not rendering the operator at all.
 */
export function getOperatorAssets<TOperator extends FilterControlOperator>(
  operator: TOperator,
): OperatorAssets {
  const assets = operatorAssets[operator];

  if (!assets) {
    return { label: operator, orderIndex: 99, requiresValues: true };
  }
  return assets;
}
